import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { connectAuthEmulator, getAuth, type Auth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, type Firestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, type Functions } from "firebase/functions";
import { connectStorageEmulator, getStorage, type FirebaseStorage } from "firebase/storage";

/**
 * `bun run dev:emulators` sets this: the app talks only to the local Firebase emulators under the
 * `demo-` project (which has no cloud resources), so nothing reaches dev or prod. See
 * scripts/seed-emulators.mjs for the seeded test account.
 */
export const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === "1";
export const EMULATOR_PROJECT_ID = "demo-asistent-auto";
const EMULATOR_HOST = "127.0.0.1";

const firebaseConfig = USE_EMULATORS
  ? {
      apiKey: "demo-key",
      authDomain: `${EMULATOR_PROJECT_ID}.firebaseapp.com`,
      projectId: EMULATOR_PROJECT_ID,
      storageBucket: `${EMULATOR_PROJECT_ID}.appspot.com`,
      appId: "demo-app",
    }
  : {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;
let storageInstance: FirebaseStorage | null = null;
let functionsInstance: Functions | null = null;

function getApp(): FirebaseApp {
  if (app) return app;
  app = getApps()[0] ?? initializeApp(firebaseConfig);
  return app;
}

export function getFirebaseAuth(): Auth {
  if (authInstance) return authInstance;
  authInstance = getAuth(getApp());
  if (USE_EMULATORS) connectAuthEmulator(authInstance, `http://${EMULATOR_HOST}:9499`, { disableWarnings: true });
  return authInstance;
}

export function getDb(): Firestore {
  if (dbInstance) return dbInstance;
  dbInstance = getFirestore(getApp());
  if (USE_EMULATORS) connectFirestoreEmulator(dbInstance, EMULATOR_HOST, 8480);
  return dbInstance;
}

export function getFirebaseStorage(): FirebaseStorage {
  if (storageInstance) return storageInstance;
  storageInstance = getStorage(getApp());
  if (USE_EMULATORS) connectStorageEmulator(storageInstance, EMULATOR_HOST, 9599);
  return storageInstance;
}

// Region matches `Firebase.functions("europe-west1")` from the Android client.
export function getFirebaseFunctions(): Functions {
  if (functionsInstance) return functionsInstance;
  functionsInstance = getFunctions(getApp(), "europe-west1");
  // The Functions emulator isn't started by dev:emulators; callables fail fast instead of reaching dev.
  if (USE_EMULATORS) connectFunctionsEmulator(functionsInstance, EMULATOR_HOST, 5001);
  return functionsInstance;
}
